import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

function clamp(value, min = 0, max = 1) {
    return Math.min(max, Math.max(min, value))
}

function getScrollContainer(root) {
    return root.closest?.('.main-scroll-area') || document.querySelector('.main-scroll-area') || window
}

function getScrollProgress(scroller = window) {
    if (scroller !== window) {
        const scrollRange = Math.max(1, scroller.scrollHeight - scroller.clientHeight)
        return clamp(scroller.scrollTop / scrollRange)
    }

    const doc = document.documentElement
    const scrollRange = Math.max(1, doc.scrollHeight - doc.clientHeight)
    return clamp(window.scrollY / scrollRange)
}

function createNativeProgressController(scroller, onProgress) {
    let frameId = null

    const update = () => {
        frameId = null
        onProgress?.(getScrollProgress(scroller))
    }

    const requestUpdate = () => {
        if (frameId !== null) return
        frameId = window.requestAnimationFrame(update)
    }

    update()
    scroller.addEventListener('scroll', requestUpdate, { passive: true })
    window.addEventListener('resize', requestUpdate)

    return () => {
        scroller.removeEventListener('scroll', requestUpdate)
        window.removeEventListener('resize', requestUpdate)
        if (frameId !== null) window.cancelAnimationFrame(frameId)
    }
}

function revealText(elements, _trigger, scroller, _cleanups, {
    start = 'top 84%',
    delay = 0,
    stagger = 0.12,
    duration = 1.22,
} = {}) {
    const targets = gsap.utils.toArray(elements)
    if (!targets.length) return

    gsap.set(targets, { autoAlpha: 0 })

    ScrollTrigger.batch(targets, {
        scroller,
        start,
        once: true,
        batchMax: 4,
        onEnter: batch => {
            gsap.to(batch, {
                autoAlpha: 1,
                duration,
                delay,
                stagger,
                ease: 'sine.out',
                clearProps: 'opacity,visibility',
                overwrite: 'auto',
            })
        },
    })
}

function revealItems(items, _trigger, scroller, {
    start = 'top 84%',
    delay = 0,
    stagger = 0.12,
    duration = 1.28,
} = {}) {
    const targets = gsap.utils.toArray(items)
    if (!targets.length) return

    gsap.set(targets, { autoAlpha: 0 })

    ScrollTrigger.batch(targets, {
        scroller,
        start,
        once: true,
        batchMax: 5,
        onEnter: batch => {
            gsap.to(batch, {
                autoAlpha: 1,
                duration,
                delay,
                stagger,
                ease: 'sine.out',
                clearProps: 'opacity,visibility',
                overwrite: 'auto',
            })
        },
    })
}

function createHeaderParallax(root, scroller) {
    const parallaxGroups = [
        {
            trigger: '.home-hero',
            pieces: [
                { selector: '.home-hero .eyebrow', y: 16, mobileY: 8 },
                { selector: '.home-hero h1', y: 42, mobileY: 22 },
                { selector: '.home-hero .hero-subtitle', y: 30, mobileY: 16 },
            ],
            start: 'top top',
            end: 'bottom top',
        },
        {
            trigger: '#gallery',
            pieces: [
                { selector: '#gallery .spotlight-copy .eyebrow', y: 8, mobileY: 5 },
                { selector: '#gallery .spotlight-copy h2', y: 22, mobileY: 12 },
                { selector: '#gallery .spotlight-copy p:not(.eyebrow)', y: 14, mobileY: 8 },
            ],
        },
        {
            trigger: '#designer',
            pieces: [
                { selector: '#designer .cinema-copy .eyebrow', y: 8, mobileY: 5 },
                { selector: '#designer .cinema-copy h2', y: 24, mobileY: 12 },
                { selector: '#designer .cinema-copy p:not(.eyebrow)', y: 15, mobileY: 8 },
            ],
        },
        {
            trigger: '#studio',
            pieces: [
                { selector: '#studio .studio-copy .eyebrow', y: 8, mobileY: 5 },
                { selector: '#studio .studio-copy h2', y: 22, mobileY: 12 },
                { selector: '#studio .studio-copy p:not(.eyebrow)', y: 14, mobileY: 8 },
            ],
        },
        {
            trigger: '#about-preview',
            pieces: [
                { selector: '#about-preview .section-heading .eyebrow', y: 8, mobileY: 5 },
                { selector: '#about-preview .section-heading h2', y: 22, mobileY: 12 },
            ],
        },
        {
            trigger: '#pricing',
            pieces: [
                { selector: '#pricing .get-started-copy .eyebrow', y: 8, mobileY: 5 },
                { selector: '#pricing .get-started-copy h2', y: 22, mobileY: 12 },
                { selector: '#pricing .get-started-copy p:not(.eyebrow)', y: 14, mobileY: 8 },
            ],
        },
        {
            trigger: '.register-cta',
            pieces: [
                { selector: '.register-cta .register-cta-copy .eyebrow', y: 7, mobileY: 4 },
                { selector: '.register-cta .register-cta-copy h2', y: 20, mobileY: 10 },
                { selector: '.register-cta .register-cta-copy p:not(.eyebrow)', y: 12, mobileY: 7 },
            ],
        },
    ]

    parallaxGroups.forEach(({ trigger, pieces, start = 'top 92%', end = 'bottom top' }) => {
        const triggerElement = root.querySelector(trigger)
        if (!triggerElement) return

        pieces.forEach(({ selector, y, mobileY }) => {
            const target = root.querySelector(selector)
            if (!target) return

            gsap.to(target, {
                y: () => window.innerWidth < 700 ? mobileY : y,
                ease: 'none',
                scrollTrigger: {
                    trigger: triggerElement,
                    scroller,
                    start,
                    end,
                    scrub: 0.85,
                    invalidateOnRefresh: true,
                },
            })
        })
    })
}

function animateHero(root) {
    const hero = root.querySelector('.home-hero')
    if (!hero) return

    const media = hero.querySelector('.hero-media')
    const titleLines = hero.querySelectorAll('.hero-title-copy-line > span')

    gsap.fromTo(media,
        { scale: 1.03, filter: 'saturate(0.92) contrast(1.04)' },
        { scale: 1, filter: 'saturate(1) contrast(1)', duration: 2.1, ease: 'sine.out' },
    )

    gsap.timeline({ defaults: { ease: 'sine.out' } })
        .fromTo(hero.querySelector('.eyebrow'), { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.95 }, 0.06)
        .fromTo(titleLines, { autoAlpha: 0 }, {
            autoAlpha: 1,
            duration: 1.25,
            stagger: 0.18,
        }, 0.18)
        .fromTo(hero.querySelector('.hero-subtitle'), { autoAlpha: 0 }, { autoAlpha: 1, duration: 1.15 }, 0.62)
        .fromTo(hero.querySelector('.hero-actions'), { autoAlpha: 0 }, {
            autoAlpha: 1,
            duration: 1,
        }, 0.82)
}

function animateGallery(root, scroller, cleanups) {
    const section = root.querySelector('#gallery')
    if (!section) return

    revealText(section.querySelectorAll('.spotlight-copy .eyebrow, .spotlight-copy h2, .spotlight-copy p:not(.eyebrow)'), section, scroller, cleanups, {
        start: 'top 76%',
    })

    revealItems(section.querySelector('.gallery-stage'), section, scroller, {
        start: 'top 76%',
        delay: 0.18,
    })

    revealItems(section.querySelectorAll('.recommend-row, .featured-voice, .voice-card'), section.querySelector('.gallery-stage'), scroller, {
        start: 'top 78%',
        delay: 0.26,
        stagger: 0.1,
    })
}

function animateDesigner(root, scroller, cleanups) {
    const section = root.querySelector('#designer')
    if (!section) return

    revealText(section.querySelectorAll('.cinema-copy .eyebrow, .cinema-copy h2, .cinema-copy p:not(.eyebrow)'), section, scroller, cleanups, {
        start: 'top 78%',
    })

    revealItems(section.querySelectorAll('.designer-step, .designer-step-line'), section, scroller, {
        start: 'top 75%',
        delay: 0.2,
        stagger: 0.12,
    })

    revealItems(section.querySelectorAll('.designer-base-card, .designer-prompt-card, .designer-history-card'), section.querySelector('.designer-workflow'), scroller, {
        start: 'top 78%',
        delay: 0.28,
        stagger: 0.12,
    })
}

function animateStudio(root, scroller, cleanups) {
    const section = root.querySelector('#studio')
    if (!section) return

    revealText(section.querySelectorAll('.studio-copy .eyebrow, .studio-copy h2, .studio-copy p:not(.eyebrow)'), section, scroller, cleanups, {
        start: 'top 78%',
    })

    revealItems(section.querySelector('.tts-demo-title'), section, scroller, {
        start: 'top 78%',
        delay: 0.18,
    })

    revealItems(section.querySelectorAll('.tts-project-demo, .tts-editor-demo, .tts-settings-demo, .tts-audio-demo'), section.querySelector('.tts-demo-shell'), scroller, {
        start: 'top 78%',
        delay: 0.26,
        stagger: 0.12,
    })
}

function animateUseCases(root, scroller, cleanups) {
    const section = root.querySelector('#about-preview')
    if (!section) return

    revealText(section.querySelectorAll('.section-heading .eyebrow, .section-heading h2'), section, scroller, cleanups, {
        start: 'top 78%',
    })
    revealItems(section.querySelectorAll('.use-case-grid article'), section, scroller, {
        start: 'top 76%',
        delay: 0.22,
        stagger: 0.12,
    })
}

function animatePricing(root, scroller, cleanups) {
    const section = root.querySelector('#pricing')
    if (!section) return

    revealText(section.querySelectorAll('.get-started-copy .eyebrow, .get-started-copy h2, .get-started-copy p:not(.eyebrow)'), section, scroller, cleanups, {
        start: 'top 78%',
    })
    revealItems(section.querySelectorAll('.home-subscription-card'), section, scroller, {
        start: 'top 76%',
        delay: 0.22,
        stagger: 0.1,
    })
}

function animateRegisterCta(root, scroller, cleanups) {
    const cta = root.querySelector('.register-cta')
    if (!cta) return

    revealText(cta.querySelectorAll('.register-cta-copy .eyebrow, .register-cta-copy h2, .register-cta-copy p:not(.eyebrow), .register-cta-action-card p'), cta, scroller, cleanups, {
        start: 'top 78%',
    })
    revealItems(cta.querySelectorAll('.register-cta-action-card, .register-cta-signal, .register-cta-actions button'), cta, scroller, {
        start: 'top 78%',
        delay: 0.2,
        stagger: 0.12,
    })
}

export function createHomeMotionController({ onProgress } = {}) {
    if (typeof window === 'undefined') return () => {}

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const root = document.querySelector('.home-page') || document.body
    const scroller = getScrollContainer(root)

    if (reduceMotion) {
        return createNativeProgressController(scroller, onProgress)
    }

    const cleanups = []
    const context = gsap.context(() => {
        ScrollTrigger.create({
            scroller,
            start: 0,
            end: () => ScrollTrigger.maxScroll(scroller),
            onUpdate: self => onProgress?.(self.progress),
            onRefresh: () => onProgress?.(getScrollProgress(scroller)),
        })

        createHeaderParallax(root, scroller)
        animateHero(root)
        animateGallery(root, scroller, cleanups)
        animateDesigner(root, scroller, cleanups)
        animateStudio(root, scroller, cleanups)
        animateUseCases(root, scroller, cleanups)
        animatePricing(root, scroller, cleanups)
        animateRegisterCta(root, scroller, cleanups)

        ScrollTrigger.refresh()
    }, root)

    return () => {
        context.revert()
        cleanups.forEach(cleanup => cleanup())
    }
}
